import { Injectable } from '@angular/core';
import { BarChart } from '../../charts/bar-chart.model';
import { ExpandedViewItem } from '../../report-data/models/sub-models/expanded-view-item.model';
import { Dataset } from '../../charts/sub-models/dataset.model';
import { Tooltip } from '../../charts/sub-models/tooltip.model';
import { List } from 'immutable';
import { ExpandedSummary } from '../../report-data/models/expanded-summary.model';
import { MoneyPipe } from '../../shared/pipes/money/money.pipe';
import { IRouter } from 'i2g-ng-ui';
import { IDeepLinkProperties } from '../../navigation/deep-link-properties.interface';
import { NavigationBuilder } from '../../navigation/navigation.builder';
import { ReportExpandedViewTypesEnum } from '../../report-data/report-data.data';
import {
  ESTIMATES_MASTER_ROUTE,
  INVOICES_MASTER_ROUTE,
} from '../../main/main.data';
import { DOCUMENTS_NEW_ROUTE } from '../../document-data/document-data.data';
import {
  IInsightsHubAdditionalProperties,
  TrackingScreenNamesEnum,
} from '../../tracking/tracking.data';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingAdditionalPropertiesBuilder } from '../../tracking/tracking-additional-properties.builder';

@Injectable()
export class InsightsSummaryBuilder {
  BAR_COLOR = '#9BCAF8';
  trackingAdditionalProperties: IInsightsHubAdditionalProperties;

  constructor(
    private _moneyPipe: MoneyPipe,
    private _navigationBuilder: NavigationBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {
    this.trackingAdditionalProperties = this._trackingAdditionalPropertiesBuilder.buildInsightsHub(
      true
    );
  }

  buildBarChart(
    summaryData: ExpandedSummary,
    summaryDataItems: List<ExpandedViewItem>,
    locale: string
  ): BarChart {
    const labels = summaryDataItems.map(
      (item: ExpandedViewItem) => item.contextLabel
    );

    const dataset = new Dataset({
      label: summaryData.title,
      urls: summaryDataItems
        .map((item: ExpandedViewItem) => item.deeplink)
        .toList(),
      data: summaryDataItems
        .map((item: ExpandedViewItem) => item.totalAmount)
        .toList(),
      bg_colors: summaryDataItems.map(() => this.BAR_COLOR).toList(),
      tooltips: summaryDataItems
        .map(
          (item: ExpandedViewItem) =>
            new Tooltip({
              label: item.contextLabel,
              summaries: List<string>([
                this._moneyPipe.transform(
                  item.totalAmount,
                  summaryData.currencyCode,
                  locale
                ),
              ]),
            })
        )
        .toList(),
    });

    return new BarChart({
      type: 'bar-chart',
      labels: labels.toJS(),
      datasets: [dataset.toJS()],
      ...summaryData.barGraph.toJS(),
    });
  }

  buildSummaryCtaRoute(
    summaryData: ExpandedSummary,
    trackingScreenName: TrackingScreenNamesEnum
  ): IRouter {
    // Hide for empty state
    if (this.isEmptySummary(summaryData)) {
      return null;
    }

    if (summaryData.cta) {
      const deeplinkProperties: IDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
        summaryData.cta.deeplink
      );

      return {
        ...this._navigationBuilder.buildRouteFromDeepLinkProperties(
          deeplinkProperties
        ),
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'see_more',
          trackingScreenName
        ),
        trackingAdditionalProperties: {
          link: summaryData.cta.deeplink,
          ...this.trackingAdditionalProperties,
        },
      };
    }

    return null;
  }

  buildEmptyStateCtaRoute(
    summaryData: ExpandedSummary,
    trackingScreenName: TrackingScreenNamesEnum
  ): IRouter {
    switch (summaryData.type) {
      case ReportExpandedViewTypesEnum.overdue:
        return {
          label:
            'HOME.INSIGHTS_HUB.OVERDUE_INVOICES_EXPANDED_VIEW.EMPTY_STATE_CTA',
          route: [INVOICES_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'see_all_invoices',
            trackingScreenName
          ),
          trackingAdditionalProperties: this.trackingAdditionalProperties,
        };

      case ReportExpandedViewTypesEnum.tax_year_monthly:
      case ReportExpandedViewTypesEnum.tax_year_quarterly:
        return {
          label: 'ACTION.ADD_INVOICE',
          route: [INVOICES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'create_invoice',
            trackingScreenName
          ),
          trackingAdditionalProperties: this.trackingAdditionalProperties,
        };

      case ReportExpandedViewTypesEnum.pending_estimates:
        return {
          label: 'ACTION.ADD_ESTIMATE',
          route: [ESTIMATES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'see_all_estimates',
            trackingScreenName
          ),
          trackingAdditionalProperties: this.trackingAdditionalProperties,
        };

      default:
        return null;
    }
  }

  isEmptySummary(summaryData: ExpandedSummary): boolean {
    return summaryData.totalAmount === 0 && summaryData.quantity === 0;
  }
}
